import { Link } from 'react-router-dom';
import type { Practitioner } from '@/mocks/practitioners';

interface PractitionerCardProps {
  practitioner: Practitioner;
  onBook: (p: Practitioner) => void;
}

export default function PractitionerCard({ practitioner, onBook }: PractitionerCardProps) {
  const p = practitioner;
  // Les praticiens Premium remontent en tête du tri : on leur donne un léger
  // accent visuel discret, sans jamais afficher leur forfait explicitement.
  const isPremium = p.subscriptionTier === 'premium';

  return (
    <article
      className={`relative overflow-hidden flex flex-col sm:flex-row gap-5 p-5 md:p-6 rounded-2xl border transition-colors ${
        isPremium
          ? 'bg-gradient-to-br from-accent-50/70 via-background-50 to-background-50 border-accent-200 hover:border-accent-300'
          : 'bg-background-50 border-background-200 hover:border-primary-300'
      }`}
    >
      {isPremium && (
        <span className="absolute left-0 top-0 bottom-0 w-1 bg-accent-400/70" aria-hidden="true" />
      )}
      <div className={`w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 flex items-center justify-center rounded-2xl ${p.color}`}>
        <span className="font-heading font-bold text-xl sm:text-2xl">{p.initials}</span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1.5">
          <h3 className="font-heading text-lg font-semibold text-foreground-950">{p.name}</h3>
          {p.rating != null && p.rating > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-[0.7rem] font-semibold">
              <i className="ri-star-fill text-accent-500 w-3.5 h-3.5 flex items-center justify-center" />
              {p.rating.toFixed(1)}
              <span className="font-normal text-accent-700/80">({p.reviews ?? 0} avis)</span>
            </span>
          )}
          {p.rating != null && p.rating >= 4.8 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-[0.65rem] font-semibold">
              <i className="ri-medal-line w-3.5 h-3.5 flex items-center justify-center" />
              Très bien noté
            </span>
          )}
          {p.isAvailableNow && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 text-primary-800 text-[0.65rem] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-soft-pulse" />
              Disponible
            </span>
          )}
        </div>
        <p className="text-sm text-primary-700 font-medium mb-1">{p.type}</p>
        <p className="text-sm text-foreground-600 flex items-center gap-1.5 mb-1">
          <i className="ri-map-pin-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
          {p.address}
        </p>
        {p.phone && (
          <p className="text-sm text-foreground-600 flex items-center gap-1.5 mb-3">
            <i className="ri-phone-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
            <a href={`tel:${p.phone.replace(/\s+/g, '')}`} className="hover:text-primary-600 cursor-pointer">
              {p.phone}
            </a>
          </p>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
          {p.price > 0 ? (
            <span className="text-sm text-foreground-800">
              Consultation dès <strong>{p.price} MAD</strong>
            </span>
          ) : (
            <span className="text-sm text-foreground-500">
              {p.category === 'Pharmacies' ? 'Tarifs en pharmacie' : 'Tarif sur demande'}
            </span>
          )}
          {p.nextSlot && (
            <span className="inline-flex items-center gap-1 text-sm text-foreground-600">
              <i className="ri-calendar-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
              {p.nextSlot}
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {p.tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium"
            >
              <i className="ri-check-line w-3.5 h-3.5 flex items-center justify-center" />
              {t}
            </span>
          ))}
        </div>
      </div>

      <div className="flex sm:flex-col items-stretch sm:items-end justify-between gap-3 sm:justify-center">
        <button
          onClick={() => onBook(p)}
          className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-calendar-check-line w-4 h-4 flex items-center justify-center" />
          Prendre RDV
        </button>
        <Link
          to={`/praticien/${p.id}`}
          className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md border border-background-300 hover:border-primary-400 text-sm font-medium text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-information-line w-4 h-4 flex items-center justify-center" />
          Fiche
        </Link>
      </div>
    </article>
  );
}